import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Alert, Badge, Tab, Tabs, Modal } from 'react-bootstrap';
import { Save, Shield, Bell, Database, Key, CreditCard, Settings } from 'react-feather';
import axios from 'axios';
import { useAuthUser } from 'react-auth-kit';

const Settingss = () => {
    const authUser = useAuthUser();
    const apiUrl = process.env.REACT_APP_URL;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('general');

    // Settings data
    const [generalSettings, setGeneralSettings] = useState({
        company_name: '',
        timezone: 'America/Mexico_City',
        date_format: 'YYYY-MM-DD',
        currency: 'USD',
        language: 'en',
        business_hours_start: '09:00',
        business_hours_end: '17:00',
        auto_logout_minutes: 60
    });

    const [securitySettings, setSecuritySettings] = useState({
        two_factor_enabled: false,
        password_expiry_days: 90,
        login_attempts_limit: 5,
        session_timeout_minutes: 30,
        require_password_change: false,
        allow_multiple_sessions: true,
        ip_whitelist: '',
        api_access_enabled: true
    });

    const [notificationSettings, setNotificationSettings] = useState({
        email_notifications: true,
        sms_notifications: false,
        push_notifications: true,
        daily_reports: true,
        weekly_reports: true,
        system_alerts: true,
        maintenance_alerts: true,
        billing_alerts: true
    });

    const [integrationSettings, setIntegrationSettings] = useState({
        google_maps_api_key: '',
        sms_provider: 'twilio',
        sms_api_key: '',
        backup_enabled: true,
        backup_frequency: 'daily',
        backup_retention_days: 30
    });

    const [billingSettings, setBillingSettings] = useState({
        subscription_plan: 'basic',
        billing_cycle: 'monthly',
        auto_renew: true,
        payment_method: '',
        billing_email: '',
        billing_address: '',
        tax_rate: 0,
        invoice_prefix: 'INV-'
    });

    const [showApiKeyModal, setShowApiKeyModal] = useState(false);
    const [apiKeys, setApiKeys] = useState([]);

    useEffect(() => {
        fetchSettings();
        fetchApiKeys();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/companies/settings`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });

            const settings = response.data;
            setGeneralSettings(settings.general || generalSettings);
            setSecuritySettings(settings.security || securitySettings);
            setNotificationSettings(settings.notifications || notificationSettings);
            setIntegrationSettings(settings.integrations || integrationSettings);
            setBillingSettings(settings.billing || billingSettings);
        } catch (err) {
            setError('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchApiKeys = async () => {
        try {
            const response = await axios.get(`${apiUrl}/companies/api-keys`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });
            setApiKeys(response.data);
        } catch (err) {
            console.error('Failed to load API keys');
        }
    };

    const saveSettings = async (settingsType, settingsData) => {
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            await axios.put(`${apiUrl}/companies/settings`, {
                type: settingsType,
                settings: settingsData
            }, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`,
                    'Content-Type': 'application/json'
                }
            });

            setSuccess(`${settingsType.charAt(0).toUpperCase() + settingsType.slice(1)} settings saved successfully!`);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    const handleGeneralSubmit = (e) => {
        e.preventDefault();
        saveSettings('general', generalSettings);
    };

    const handleSecuritySubmit = (e) => {
        e.preventDefault();
        saveSettings('security', securitySettings);
    };

    const handleNotificationSubmit = (e) => {
        e.preventDefault();
        saveSettings('notifications', notificationSettings);
    };

    const handleIntegrationSubmit = (e) => {
        e.preventDefault();
        saveSettings('integrations', integrationSettings);
    };

    const handleBillingSubmit = (e) => {
        e.preventDefault();
        saveSettings('billing', billingSettings);
    };

    const generateApiKey = async () => {
        try {
            const response = await axios.post(`${apiUrl}/companies/api-keys`, {
                name: `API Key ${apiKeys.length + 1}`,
                permissions: ['read', 'write']
            }, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`,
                    'Content-Type': 'application/json'
                }
            });

            setSuccess('API key generated successfully!');
            fetchApiKeys();
        } catch (err) {
            setError('Failed to generate API key');
        }
    };

    const revokeApiKey = async (keyId) => {
        try {
            await axios.delete(`${apiUrl}/companies/api-keys/${keyId}`, {
                headers: {
                    'Authorization': `Bearer ${authUser()?.token || localStorage.getItem('_auth')}`
                }
            });

            setSuccess('API key revoked successfully!');
            fetchApiKeys();
        } catch (err) {
            setError('Failed to revoke API key');
        }
    };

    return (
        <div className="main-content">
            <div className="container-fluid">
                <Row>
                    <Col>
                        <div className="page-title-box">
                            <h4 className="mb-sm-0">Company Settings</h4>
                        </div>
                    </Col>
                </Row>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Row>
                    <Col>
                        <Card>
                            <Card.Body>
                                <Tabs
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k)}
                                    className="mb-4"
                                >
                                    {/* General Settings Tab */}
                                    <Tab eventKey="general" title={
                                        <span><Settingss size={16} className="me-1" />General</span>
                                    }>
                                        <Form onSubmit={handleGeneralSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Company Name</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={generalSettings.company_name}
                                                            onChange={(e) => setGeneralSettings(prev => ({
                                                                ...prev, company_name: e.target.value
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Timezone</Form.Label>
                                                        <Form.Select
                                                            value={generalSettings.timezone}
                                                            onChange={(e) => setGeneralSettings(prev => ({
                                                                ...prev, timezone: e.target.value
                                                            }))}
                                                        >
                                                            <option value="America/Mexico_City">Mexico City</option>
                                                            <option value="UTC">UTC</option>
                                                            <option value="America/New_York">Eastern Time</option>
                                                            <option value="America/Chicago">Central Time</option>
                                                            <option value="America/Denver">Mountain Time</option>
                                                            <option value="America/Los_Angeles">Pacific Time</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Date Format</Form.Label>
                                                        <Form.Select
                                                            value={generalSettings.date_format}
                                                            onChange={(e) => setGeneralSettings(prev => ({
                                                                ...prev, date_format: e.target.value
                                                            }))}
                                                        >
                                                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Currency</Form.Label>
                                                        <Form.Select
                                                            value={generalSettings.currency}
                                                            onChange={(e) => setGeneralSettings(prev => ({
                                                                ...prev, currency: e.target.value
                                                            }))}
                                                        >
                                                            <option value="USD">USD - US Dollar</option>
                                                            <option value="EUR">EUR - Euro</option>
                                                            <option value="MXN">MXN - Mexican Peso</option>
                                                            <option value="CAD">CAD - Canadian Dollar</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Business Hours Start</Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={generalSettings.business_hours_start}
                                                            onChange={(e) => setGeneralSettings(prev => ({
                                                                ...prev, business_hours_start: e.target.value
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Business Hours End</Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={generalSettings.business_hours_end}
                                                            onChange={(e) => setGeneralSettings(prev => ({
                                                                ...prev, business_hours_end: e.target.value
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <div className="text-end">
                                                <Button variant="primary" type="submit" disabled={loading}>
                                                    <Save size={16} className="me-1" />
                                                    {loading ? 'Saving...' : 'Save General Settings'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Tab>

                                    {/* Security Settings Tab */}
                                    <Tab eventKey="security" title={
                                        <span><Shield size={16} className="me-1" />Security</span>
                                    }>
                                        <Form onSubmit={handleSecuritySubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Enable Two-Factor Authentication"
                                                            checked={securitySettings.two_factor_enabled}
                                                            onChange={(e) => setSecuritySettings(prev => ({
                                                                ...prev, two_factor_enabled: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Allow Multiple Sessions"
                                                            checked={securitySettings.allow_multiple_sessions}
                                                            onChange={(e) => setSecuritySettings(prev => ({
                                                                ...prev, allow_multiple_sessions: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Password Expiry (days)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={securitySettings.password_expiry_days}
                                                            onChange={(e) => setSecuritySettings(prev => ({
                                                                ...prev, password_expiry_days: parseInt(e.target.value)
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Login Attempts Limit</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={securitySettings.login_attempts_limit}
                                                            onChange={(e) => setSecuritySettings(prev => ({
                                                                ...prev, login_attempts_limit: parseInt(e.target.value)
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3">
                                                <Form.Label>IP Whitelist (comma-separated)</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    value={securitySettings.ip_whitelist}
                                                    onChange={(e) => setSecuritySettings(prev => ({
                                                        ...prev, ip_whitelist: e.target.value
                                                    }))}
                                                    placeholder="192.168.1.1, 10.0.0.1"
                                                />
                                            </Form.Group>

                                            <div className="text-end">
                                                <Button variant="primary" type="submit" disabled={loading}>
                                                    <Save size={16} className="me-1" />
                                                    {loading ? 'Saving...' : 'Save Security Settings'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Tab>

                                    {/* Notifications Tab */}
                                    <Tab eventKey="notifications" title={
                                        <span><Bell size={16} className="me-1" />Notifications</span>
                                    }>
                                        <Form onSubmit={handleNotificationSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <h6 className="mb-3">Communication Preferences</h6>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Email Notifications"
                                                            checked={notificationSettings.email_notifications}
                                                            onChange={(e) => setNotificationSettings(prev => ({
                                                                ...prev, email_notifications: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="SMS Notifications"
                                                            checked={notificationSettings.sms_notifications}
                                                            onChange={(e) => setNotificationSettings(prev => ({
                                                                ...prev, sms_notifications: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Push Notifications"
                                                            checked={notificationSettings.push_notifications}
                                                            onChange={(e) => setNotificationSettings(prev => ({
                                                                ...prev, push_notifications: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <h6 className="mb-3">Alert Types</h6>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="System Alerts"
                                                            checked={notificationSettings.system_alerts}
                                                            onChange={(e) => setNotificationSettings(prev => ({
                                                                ...prev, system_alerts: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Maintenance Alerts"
                                                            checked={notificationSettings.maintenance_alerts}
                                                            onChange={(e) => setNotificationSettings(prev => ({
                                                                ...prev, maintenance_alerts: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Billing Alerts"
                                                            checked={notificationSettings.billing_alerts}
                                                            onChange={(e) => setNotificationSettings(prev => ({
                                                                ...prev, billing_alerts: e.target.checked
                                                            }))}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <div className="text-end">
                                                <Button variant="primary" type="submit" disabled={loading}>
                                                    <Save size={16} className="me-1" />
                                                    {loading ? 'Saving...' : 'Save Notification Settings'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Tab>

                                    {/* API & Integrations Tab */}
                                    <Tab eventKey="integrations" title={
                                        <span><Key size={16} className="me-1" />API & Integrations</span>
                                    }>
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6>API Keys</h6>
                                                <Button variant="outline-primary" size="sm" onClick={generateApiKey}>
                                                    Generate New Key
                                                </Button>
                                            </div>

                                            {apiKeys.length > 0 ? (
                                                <div className="border rounded p-3">
                                                    {apiKeys.map((key, index) => (
                                                        <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                                            <div>
                                                                <strong>{key.name}</strong>
                                                                <span className="text-muted ms-2">
                                                                    {key.key_preview}...
                                                                </span>
                                                                <Badge bg="secondary" className="ms-2">
                                                                    {key.permissions?.join(', ')}
                                                                </Badge>
                                                            </div>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => revokeApiKey(key.id)}
                                                            >
                                                                Revoke
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-muted">No API keys generated yet.</p>
                                            )}
                                        </div>

                                        <Form onSubmit={handleIntegrationSubmit}>
                                            <h6 className="mb-3">Third-party Integrations</h6>

                                            <Form.Group className="mb-3">
                                                <Form.Label>Google Maps API Key</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={integrationSettings.google_maps_api_key}
                                                    onChange={(e) => setIntegrationSettings(prev => ({
                                                        ...prev, google_maps_api_key: e.target.value
                                                    }))}
                                                    placeholder="Enter Google Maps API key"
                                                />
                                            </Form.Group>

                                            <div className="text-end">
                                                <Button variant="primary" type="submit" disabled={loading}>
                                                    <Save size={16} className="me-1" />
                                                    {loading ? 'Saving...' : 'Save Integration Settings'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Tab>
                                </Tabs>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default Settingss;
